import type {
  AccessLevelProp,
  CSS,
  FadeEdge,
  GutterConfig,
  GutterEdge,
  SpacingScale,
} from '@meterup/atto';
import type { ColumnFiltersState } from '@tanstack/table-core';
import type { ReactNode } from 'react';
import {
  Button,
  ClearAccessLevelContext,
  ControlGroup,
  Divider,
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuCheckboxItem,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPopover,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  fadeMaskCSS,
  gutterCSS,
  HorizontalScrollShadow,
  HStack,
  Icon,
  LoadingIcon,
  sizing,
  styled,
  Text,
  useHorizontalShadowOnScroll,
  useVerticalOverflowEdges,
} from '@meterup/atto';
import { useMeasure } from '@uidotdev/usehooks';
import { isNumber } from 'lodash-es';
import { useCallback, useEffect, useEffectEvent, useMemo, useState } from 'react';

import type {
  FilterComponentFunction,
  FilterComponentProps,
  FilterOption,
  FilterOptions,
  FilterValue,
  FilterVariant,
  NormalizedFilterOption,
  SharedColumnDef,
} from '../createColumnBuilder';
import type { AutoCollectionStateInterface, Filter, Table } from '../utils';
import type { TableFiltersActions } from './TableFiltersActions';
import type { TableFiltersTokenProps } from './TableFiltersToken';
import { isDefined } from '../../../helpers/isDefined';
import { sortingCollator } from '../../../helpers/utils/strings';
import { parseDateTime } from '../../../topics/time';
import { DateTimeRangeStorageProvider } from '../../DateTimeRangePicker/DateTimeRangeStorageContext';
import { FormatDateTime } from '../../FormatDateTime';
import { KeyBoundSearchInput } from '../../KeyBoundSearchInput';
import { TableColumnsEnhanced } from '../TableColumns/TableColumns';
import { TablePresets } from '../TablePresets/TablePresets';
import { isTableSortOrder, TableFilterVariant, TableSortOrderType } from '../utils';
import { TableFiltersActionsMenu } from './TableFiltersActions';
import {
  TableFiltersFieldDateRange,
  TableFiltersFieldMultiSelect,
  TableFiltersFieldNumber,
  TableFiltersFieldNumberRange,
  TableFiltersFieldSelect,
  TableFiltersFieldText,
} from './TableFiltersField/TableFiltersField';
import { ServerTableFiltersToken, TableFiltersToken } from './TableFiltersToken';
import { isFilterApplied } from './utils';

const tableFiltersVars = {
  padding: {
    sides: 8,
    ends: 6,
  },
} satisfies {
  padding: {
    sides: SpacingScale;
    ends: SpacingScale;
  };
};
const DEFAULT_OUTER_GUTTER = { context: 'application' } satisfies GutterConfig;

// Steps from 3.5 to 5.5 rows as the viewport gets taller. A row is a small token plus the gap (32px);
// the extra 16px is the top padding plus a 10px peek of the next row.
const NARROW_OPTIONS_MAX_HEIGHT =
  'calc(round(down, clamp(96px, 16svh - 16px, 160px), 32px) + 16px)';

const outerGutterCSS = (
  edge: Extract<GutterEdge, 'start' | 'end'>,
  gutter: GutterConfig = DEFAULT_OUTER_GUTTER,
): CSS => {
  if (gutter.edge === 'none') {
    return {};
  }

  return gutterCSS({ ...gutter, edge, placement: 'outside' });
};

function verticalFadeEdge({ top, bottom }: { top: boolean; bottom: boolean }): FadeEdge {
  if (top && bottom) return 'vertical';
  if (top) return 'top';
  if (bottom) return 'bottom';
  return 'none';
}

const narrowOptionsCSS = (
  gutter: GutterConfig | undefined,
  overflow: { top: boolean; bottom: boolean },
): CSS => ({
  ...outerGutterCSS('start', gutter),
  ...outerGutterCSS('end', gutter),
  ...fadeMaskCSS(`$${tableFiltersVars.padding.ends}`, verticalFadeEdge(overflow)),
});

function useOptionsScroll() {
  const [horizontalScrollShadow, horizontalRef] = useHorizontalShadowOnScroll('both');
  const [verticalOverflow, verticalRef] = useVerticalOverflowEdges();
  // One element feeds both hooks. Not `mergeRefs`: it drops the ref cleanups that detach their listeners.
  const ref = useCallback(
    (element: HTMLElement | null) => {
      const cleanups = [horizontalRef(element), verticalRef(element)];
      return () => {
        for (const cleanup of cleanups) {
          if (typeof cleanup === 'function') cleanup();
        }
      };
    },
    [horizontalRef, verticalRef],
  );
  return { horizontalScrollShadow, verticalOverflow, ref };
}

interface SelectedFiltersDropdownOption {
  value: string;
  label: string;
  group?: string | null;
  accessLevel?: AccessLevelProp | null | undefined;
}

interface SelectedFiltersDropdownProps {
  options: Array<SelectedFiltersDropdownOption>;
  selectedFilters: string[];
  setSelectedFilters: (filters: string[]) => void;
  groupOrder?: Map<string, number>;
  loading?: boolean;
  /** When true, sort options within each group alphabetically by label. */
  sortOptions?: boolean;
  disabled?: boolean;
}

const DEFAULT_FILTER_GROUP_NAME = 'Filters';

function DisplayForOption({ option }: { option: FilterOption }) {
  return option.label ?? option.textValue ?? option.value;
}

function DisplayForFilterValue({
  value,
  options,
  variant,
  loading,
}: {
  value: FilterValue;
  options?: FilterOption[];
  variant?: FilterVariant;
  loading?: boolean;
}): ReactNode {
  if (loading) {
    return (
      <HStack role="status" aria-label="Loading filter options">
        <LoadingIcon size={16} />
      </HStack>
    );
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (!value) {
    return 'All';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return 'All';
    }

    if (options) {
      const selectedOptions = value
        .map((v) => options.find((opt) => opt.value === v))
        .filter(isDefined);
      if (!selectedOptions.length) return '(Unknown option)';

      return (
        <HStack spacing={4}>
          {selectedOptions.map((option, index) => (
            <HStack key={option.value} wrap="nowrap" align="center">
              <DisplayForOption option={option} />
              {index < value.length - 1 ? <Text>,</Text> : null}
            </HStack>
          ))}
        </HStack>
      );
    }

    if (variant !== 'multi-select') {
      if (value[0] === null && value[1] === null) {
        return null;
      }
      if (isNumber(value[0]) || isNumber(value[1])) {
        return (
          <HStack align="center" spacing={4} wrap="nowrap">
            {value[0]}
            <Icon icon="arrow-right" size={10} />
            {value[1]}
          </HStack>
        );
      }

      const startDate = parseDateTime(value[0]);
      const endDate = parseDateTime(value[1]);
      if (startDate || endDate) {
        return (
          <HStack align="center" spacing={4} wrap="nowrap">
            <FormatDateTime value={startDate} />
            <Icon icon="arrow-right" size={10} />
            <FormatDateTime value={endDate} />
          </HStack>
        );
      }
    }

    return value.join(', ');
  }

  if (options) {
    const option = options.find((opt) => opt.value === value);
    if (option) {
      return <DisplayForOption option={option} />;
    }

    return null;
  }

  return value;
}

function SelectedFiltersDropdown({
  options,
  selectedFilters,
  setSelectedFilters,
  groupOrder,
  loading,
  sortOptions,
  disabled,
}: SelectedFiltersDropdownProps) {
  const groupedOptions = useMemo(() => {
    const groups = new Map<string, Array<SelectedFiltersDropdownOption>>();

    for (const opt of options) {
      const groupName = opt.group ?? DEFAULT_FILTER_GROUP_NAME;
      let arr = groups.get(groupName);
      if (!arr) {
        arr = [];
        groups.set(groupName, arr);
      }
      arr.push(opt);
    }

    if (sortOptions) {
      for (const [, arr] of groups) {
        arr.sort((a, b) => a.label.localeCompare(b.label));
      }
    }

    return Array.from(groups.entries()).sort(([groupA], [groupB]) => {
      if (groupA === DEFAULT_FILTER_GROUP_NAME && groupB !== DEFAULT_FILTER_GROUP_NAME) return -1;
      if (groupA !== DEFAULT_FILTER_GROUP_NAME && groupB === DEFAULT_FILTER_GROUP_NAME) return 1;

      // If groupOrder is provided, sort by column order; otherwise fall back to alphabetical
      if (groupOrder) {
        const orderA = groupOrder.get(groupA) ?? Infinity;
        const orderB = groupOrder.get(groupB) ?? Infinity;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
      }

      return groupA.localeCompare(groupB);
    });
  }, [options, groupOrder, sortOptions]);

  return (
    <DropdownMenu disabled={disabled}>
      <DropdownMenuButton
        tabIndex={0}
        appearance="muted"
        arrangement="hidden-label"
        icon="filter"
        size="small"
        loading={loading}
        disabled={disabled}
      >
        Add a filter...
      </DropdownMenuButton>
      <DropdownMenuPopover align="start">
        <ClearAccessLevelContext>
          {groupedOptions.map(([groupName, groupOptions]) => (
            <DropdownMenuGroup key={groupName} label={groupName}>
              {groupOptions.map((opt) => {
                const isSelected = selectedFilters.includes(opt.value);
                return (
                  <DropdownMenuCheckboxItem
                    key={opt.value}
                    checked={isSelected}
                    // Adding a filter auto-opens its token popover, so close rather than stack.
                    closeOnClick={!isSelected}
                    onCheckedChange={(checked) => {
                      const newVal = checked
                        ? [...selectedFilters, opt.value]
                        : selectedFilters.filter((selected) => selected !== opt.value);
                      setSelectedFilters(newVal);
                    }}
                    accessLevel={opt.accessLevel}
                  >
                    {opt.label}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuGroup>
          ))}
        </ClearAccessLevelContext>
      </DropdownMenuPopover>
    </DropdownMenu>
  );
}

const TableFiltersDivider = styled(Divider, {
  padding: '$4 $2',
});
TableFiltersDivider.displayName = 'TableFiltersDivider';

const TableFiltersViews = styled(ControlGroup, {
  gridArea: 'views',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$8',
  padding: `$${tableFiltersVars.padding.ends} $${tableFiltersVars.padding.sides / 2}`,
});
TableFiltersViews.displayName = 'TableFiltersViews';

const TableFiltersOptionsScroll = styled(HorizontalScrollShadow, {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$8',
  maxWidth: '100%',
  padding: `$${tableFiltersVars.padding.ends} ${sizing.sides}`,

  variants: {
    hasControls: {
      true: {},
      false: {},
    },
    hasViews: {
      true: {},
      false: {},
    },
    isNarrow: {
      true: {
        flexWrap: 'wrap',
        paddingX: 0,
        maxHeight: NARROW_OPTIONS_MAX_HEIGHT,
      },
      false: {},
    },
  },

  compoundVariants: [
    {
      hasControls: true,
      isNarrow: false,
      css: {
        paddingRight: `$${tableFiltersVars.padding.sides / 2}`,
      },
    },
    {
      hasViews: true,
      isNarrow: false,
      css: {
        paddingLeft: `$${tableFiltersVars.padding.sides / 2}`,
      },
    },
  ],
});
TableFiltersOptionsScroll.displayName = 'TableFiltersOptionsScroll';

const TableFiltersOptions = styled('div', {
  gridArea: 'options',
  position: 'relative',
  maxWidth: '100%',
  minWidth: 0,
});
TableFiltersOptions.displayName = 'TableFiltersOptions';

const TableFiltersControls = styled(ControlGroup, {
  gridArea: 'controls',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '$8',
  padding: `$${tableFiltersVars.padding.ends} $${tableFiltersVars.padding.sides / 2}`,
});
TableFiltersControls.displayName = 'TableFiltersControls';

const TableFiltersContainer = styled('div', {
  display: 'grid',
  width: '100%',

  variants: {
    isNarrow: {
      true: {
        display: 'flex',
        flexWrap: 'wrap',

        [`& > ${TableFiltersControls}`]: {
          width: 'min-content',
          marginLeft: 'auto',
        },

        [`& > ${TableFiltersOptions}`]: {
          order: 1,
          flexBasis: '100%',
        },
      },
      false: {
        gridTemplateColumns: 'min-content 1fr min-content',
        gridTemplateAreas: `
          "views options controls"
        `,
        width: '100%',
      },
    },
  },
});
TableFiltersContainer.displayName = 'TableFiltersContainer';

type FilterMapVal<RowType> = Omit<
  FilterComponentProps<RowType>,
  'handleFiltersChange' | 'onKeyDown'
> & {
  label: string;
  filter?: FilterComponentFunction<RowType>;
  variant?: FilterVariant;
  options?: FilterOptions<RowType>;
  optionsLoading?: boolean;
  dateRangePresets?: boolean;
  pinned?: boolean;
  accessLevel?: AccessLevelProp | null | undefined;
};

function TableFilter<RowType>({
  mappedFilter,
  options,
  handleFiltersChange,
  handleFilterValueSelected,
}: {
  mappedFilter: FilterMapVal<RowType>;
  options: FilterOption[] | undefined;
  handleFiltersChange: (id: string, value: FilterValue) => void;
  handleFilterValueSelected?: (id: string) => void;
}) {
  if (mappedFilter?.filter) {
    return mappedFilter.filter({
      ...mappedFilter,
      handleFiltersChange,
      onKeyDown: () => {},
    });
  }

  switch (mappedFilter.variant) {
    case 'select':
      return (
        <TableFiltersFieldSelect
          {...mappedFilter}
          options={options!}
          value={mappedFilter.value as string}
          handleFiltersChange={handleFiltersChange}
          onKeyDown={() => {}}
          onValueSelected={
            handleFilterValueSelected ? () => handleFilterValueSelected(mappedFilter.id) : undefined
          }
        />
      );
    case 'multi-select':
      return (
        <TableFiltersFieldMultiSelect
          {...mappedFilter}
          options={options!}
          value={mappedFilter.value as string[]}
          handleFiltersChange={handleFiltersChange}
          onKeyDown={() => {}}
        />
      );
    case 'number-range':
      return (
        <TableFiltersFieldNumberRange
          {...mappedFilter}
          value={mappedFilter.value as [number | null, number | null]}
          handleFiltersChange={handleFiltersChange}
          onKeyDown={() => {}}
          onValueSelected={
            handleFilterValueSelected ? () => handleFilterValueSelected(mappedFilter.id) : undefined
          }
        />
      );
    case 'number':
      return (
        <TableFiltersFieldNumber
          {...mappedFilter}
          value={mappedFilter.value as number | null}
          handleFiltersChange={handleFiltersChange}
          onKeyDown={() => {}}
          onValueSelected={
            handleFilterValueSelected ? () => handleFilterValueSelected(mappedFilter.id) : undefined
          }
        />
      );
    case 'text':
      return (
        <TableFiltersFieldText
          {...mappedFilter}
          value={mappedFilter.value as string}
          onChange={handleFiltersChange}
          onKeyDown={() => {}}
          onValueSelected={
            handleFilterValueSelected ? () => handleFilterValueSelected(mappedFilter.id) : undefined
          }
        />
      );
    case 'date-range':
      return (
        <DateTimeRangeStorageProvider>
          <TableFiltersFieldDateRange
            {...mappedFilter}
            value={mappedFilter.value as [string | null, string | null]}
            handleFiltersChange={handleFiltersChange}
            onKeyDown={() => {}}
            onValueSelected={
              handleFilterValueSelected
                ? () => handleFilterValueSelected(mappedFilter.id)
                : undefined
            }
            showPresets={mappedFilter.dateRangePresets !== false}
          />
        </DateTimeRangeStorageProvider>
      );
    default:
      return null;
  }
}

function normalizeFilterOption(option: FilterOption | string): NormalizedFilterOption {
  if (typeof option === 'string') {
    return {
      value: option,
      label: option,
    };
  }

  if (!option.label) {
    return {
      ...option,
      label: option.value,
    };
  }

  return option as NormalizedFilterOption;
}

function getFilterOptionTextValue(option: NormalizedFilterOption): string {
  if (option.textValue) return option.textValue;
  if (typeof option.label === 'string') return option.label;
  return option.value;
}

function normalizeFilterOptions<RowType>(
  data: RowType[] | undefined,
  filterOptions: FilterOptions<RowType> | undefined,
): NormalizedFilterOption[] | undefined {
  if (!filterOptions) {
    return undefined;
  }

  if (Array.isArray(filterOptions)) {
    return filterOptions.map(normalizeFilterOption);
  }

  const options = filterOptions(data)?.map(normalizeFilterOption) ?? [];
  const map = new Map<string, NormalizedFilterOption>();
  for (const option of options) {
    if (!map.has(option.value)) {
      map.set(option.value, option);
    }
  }

  return Array.from(map.values()).sort((a, b) =>
    sortingCollator.compare(getFilterOptionTextValue(a), getFilterOptionTextValue(b)),
  );
}

function initValForVariant(variant?: FilterVariant): unknown {
  if (variant) {
    switch (variant) {
      case 'select':
        return null;
      case 'multi-select':
        return [];
      case 'text':
        return '';
      case 'number-range':
      case 'date-range':
        return [null, null];
      case 'number':
        return null;
      case 'boolean':
        return true;
      default:
        return null;
    }
  }
  return null;
}

export function TableFilters<RowType>({
  columns = [],
  data,
  filtersState = [],
  onFiltersChange = () => {},
  onClearFilters,
  controls,
  staticFilters,
  isNarrow: isNarrowProp,
  viewGutter,
}: {
  columns?: SharedColumnDef<RowType, any>[];
  data?: RowType[];
  filtersState?: ColumnFiltersState;
  onFiltersChange?: (columnState: ColumnFiltersState) => void;
  onClearFilters?: React.MouseEventHandler<HTMLButtonElement>;
  controls?: ReactNode;
  staticFilters?: ReactNode;
  isNarrow?: boolean;
  viewGutter?: GutterConfig;
}) {
  const optionsScroll = useOptionsScroll();
  const [openFilters, setOpenFilters] = useState<Set<string>>(new Set());

  const filters: FilterMapVal<RowType>[] = useMemo(() => {
    const filterList: FilterMapVal<RowType>[] = [];

    for (const col of columns) {
      // Add main column filter if it has filter configuration
      if (col && (col.filterFn || col.filter) && col.filterVariant) {
        const label = (col.filterLabel ?? col.meta?.label ?? col.header) as string;
        const value = filtersState?.find((filter) => filter.id === col.id)?.value as FilterValue;

        filterList.push({
          id: col.id,
          value,
          label,
          data,
          filter: col.filter,
          options: col.filterOptions,
          optionsLoading: col.filterOptionsLoading,
          variant: col.filterVariant,
          dateRangePresets: col.filterDateRangePresets,
        });
      }

      // Add subline filter if column has subline and sublineFilterVariant is defined
      if (
        col?.meta?.subline &&
        col.sublineFilterVariant &&
        (col.sublineFilterFn || col.sublineFilter)
      ) {
        const sublineId = `${col.id}-subline`;
        const baseLabel = col.filterLabel ?? col.meta?.label ?? col.header;
        const label = col.sublineFilterLabel ?? (baseLabel ? `${baseLabel} (subline)` : 'Subline');
        const value = filtersState?.find((filter) => filter.id === sublineId)?.value as FilterValue;

        filterList.push({
          id: sublineId,
          value,
          label,
          data,
          filter: col.sublineFilter,
          options: col.sublineFilterOptions,
          optionsLoading: col.sublineFilterOptionsLoading,
          variant: col.sublineFilterVariant,
        });
      }
    }

    return filterList;
  }, [columns, filtersState, data]);

  const filtersMap = useMemo(
    () => new Map<string, FilterMapVal<RowType>>(filters.map((f) => [f.id, f])),
    [filters],
  );

  const filterOptionsResult = useMemo(() => {
    const optionsMap = new Map<string, SelectedFiltersDropdownOption>();
    const groupOrderMap = new Map<string, number>();
    let columnIndex = 0;

    for (const col of columns) {
      // Add main column filter option
      if ((col?.filterFn || col?.filter) && col?.filterVariant && col.id) {
        const label = (col.filterLabel ?? col.header) as string;
        const group = col.meta?.group ?? null;
        const groupName = group ?? DEFAULT_FILTER_GROUP_NAME;

        // Track the first occurrence of each group
        if (!groupOrderMap.has(groupName)) {
          groupOrderMap.set(groupName, columnIndex);
        }

        optionsMap.set(col.id, {
          value: col.id,
          label,
          group,
          accessLevel: col.meta?.accessLevel,
        });
        columnIndex += 1;
      }

      // Add subline filter option if column has subline and sublineFilterVariant
      if (
        col?.meta?.subline &&
        col.sublineFilterVariant &&
        (col.sublineFilterFn || col.sublineFilter) &&
        col.id
      ) {
        const sublineId = `${col.id}-subline`;
        const baseLabel = col.filterLabel ?? col.header;
        const label = col.sublineFilterLabel ?? (baseLabel ? `${baseLabel} (subline)` : 'Subline');
        const group = col.meta?.group ?? null;
        const groupName = group ?? DEFAULT_FILTER_GROUP_NAME;

        // Track the first occurrence of each group (use the same index as parent column)
        if (!groupOrderMap.has(groupName)) {
          groupOrderMap.set(groupName, columnIndex - 1);
        }

        optionsMap.set(sublineId, {
          value: sublineId,
          label,
          group,
        });
      }
    }
    return { options: Array.from(optionsMap.values()), groupOrder: groupOrderMap };
  }, [columns]);

  const { options: filterOptions, groupOrder } = filterOptionsResult;

  const handleFiltersChange = useCallback(
    (id: string, value: FilterValue) => {
      let newFilters = [];
      const prevFilter = filtersState.find((col) => col.id === id);
      if (prevFilter) {
        newFilters =
          filtersState.map((col) => {
            if (col.id === id) {
              return {
                ...col,
                value,
              };
            }
            return { ...col };
          }) ?? [];
      } else {
        newFilters = [...filtersState, { id, value }];
      }
      onFiltersChange(newFilters);
    },
    [filtersState, onFiltersChange],
  );

  const handleSelectedFiltersChange = useCallback(
    (ids: string[]) => {
      // Find newly added filters
      const currentFilterIds = new Set(filtersState?.map((filter) => filter.id) || []);
      const newlyAddedFilters = ids.filter((id) => !currentFilterIds.has(id));

      const newFilters = [];
      for (const id of ids) {
        const prevFilter = filtersState?.find((filter) => filter.id === id);
        if (prevFilter) {
          newFilters.push(prevFilter);
        } else {
          const newFilter = filtersMap.get(id);
          if (newFilter) {
            newFilters.push({
              id,
              value: initValForVariant(newFilter?.variant),
            });
          }
        }
      }

      // Automatically open newly added filters
      if (newlyAddedFilters.length > 0) {
        setOpenFilters((prev) => new Set([...prev, ...newlyAddedFilters]));
      }

      onFiltersChange(newFilters);
    },
    [filtersMap, filtersState, onFiltersChange],
  );

  const handleClearFilters = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onFiltersChange([]);
      if (onClearFilters) {
        onClearFilters(e);
      }
    },
    [onFiltersChange, onClearFilters],
  );

  const handleClearFilter = useCallback(
    (id: string) => {
      const filteredIds =
        filtersState?.filter((filter) => filter.id !== id).map((filter) => filter.id) ?? [];
      handleSelectedFiltersChange(filteredIds);
    },
    [filtersState, handleSelectedFiltersChange],
  );

  const handleFilterOpenChange = useCallback((id: string, isOpen: boolean) => {
    setOpenFilters((prev) => {
      const newSet = new Set(prev);
      if (isOpen) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }, []);

  const handleFilterValueSelected = useCallback((id: string) => {
    setOpenFilters((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  }, []);

  const filtersStateWithOptions = useMemo(
    () =>
      filtersState.map((filter) => {
        const mappedFilter = filtersMap.get(filter.id);
        const finalOptions = normalizeFilterOptions(data, mappedFilter?.options);

        return {
          ...filter,
          mappedFilter,
          finalOptions,
        };
      }),
    [data, filtersMap, filtersState],
  );

  const updateFilterValue = useEffectEvent((columnID: string, value: FilterValue) => {
    handleFiltersChange(columnID, value);
  });

  useEffect(() => {
    for (const { id, finalOptions, value, mappedFilter } of filtersStateWithOptions) {
      if (mappedFilter?.optionsLoading || !finalOptions || !Array.isArray(value)) continue;
      const optionsMap = new Map(finalOptions.map((opt) => [opt.value, opt]));
      const values = value
        .map((v) => optionsMap.get(v))
        .filter(isDefined)
        .map((opt) => opt.value);
      if (values.length !== value.length) {
        updateFilterValue(id, values);
      }
    }
  }, [filtersMap, columns, filtersStateWithOptions]);

  const hasColumns = Boolean(columns?.length !== 0);
  const hasFilters = Boolean(hasColumns && filtersState?.length > 0);
  const hasControls = Boolean(controls || hasFilters);
  const canAddFilter = filtersState.length < filterOptions.length;

  const [ref, { width }] = useMeasure();
  const isNarrow = isNarrowProp ?? Boolean(width && width < 700);

  const showNarrowAddFilter = isNarrow && hasColumns && filters.length > 0 && canAddFilter;
  const hasViews = Boolean(staticFilters) || showNarrowAddFilter;

  return (
    <TableFiltersContainer ref={ref} isNarrow={isNarrow}>
      {hasViews && (
        <TableFiltersViews
          relation="separate"
          size="small"
          css={outerGutterCSS('start', viewGutter)}
        >
          {staticFilters}
          {staticFilters && hasColumns && (
            <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
          )}
          {showNarrowAddFilter && (
            <SelectedFiltersDropdown
              options={filterOptions}
              selectedFilters={filtersState.map((filter) => filter.id)}
              setSelectedFilters={handleSelectedFiltersChange}
              groupOrder={groupOrder}
            />
          )}
        </TableFiltersViews>
      )}
      {hasColumns && filters.length > 0 && (!isNarrow || hasFilters) && (
        <TableFiltersOptions>
          <TableFiltersOptionsScroll
            isNarrow={isNarrow}
            css={
              isNarrow ? narrowOptionsCSS(viewGutter, optionsScroll.verticalOverflow) : undefined
            }
            hasViews={hasViews}
            hasControls={hasControls}
            ref={optionsScroll.ref}
            {...optionsScroll.horizontalScrollShadow}
          >
            <ClearAccessLevelContext>
              {filtersStateWithOptions.map(({ mappedFilter, finalOptions: options }) => {
                if (mappedFilter) {
                  const sharedTokenProps = {
                    key: mappedFilter.id,
                    handle: mappedFilter.label,
                    value: (
                      <DisplayForFilterValue
                        value={mappedFilter.value}
                        options={options}
                        variant={mappedFilter.variant}
                        loading={mappedFilter.optionsLoading}
                      />
                    ),
                    onTokenClear: () => handleClearFilter(mappedFilter.id),
                    modified: isFilterApplied(mappedFilter.value),
                  };

                  return (
                    <TableFiltersToken
                      {...sharedTokenProps}
                      changeable
                      isOpen={openFilters.has(mappedFilter.id)}
                      onOpenChange={(isOpen) => handleFilterOpenChange(mappedFilter.id, isOpen)}
                    >
                      <TableFilter
                        mappedFilter={mappedFilter}
                        options={options!}
                        handleFiltersChange={handleFiltersChange}
                        handleFilterValueSelected={handleFilterValueSelected}
                      />
                    </TableFiltersToken>
                  );
                }
                return null;
              })}
            </ClearAccessLevelContext>
            {!isNarrow && canAddFilter && (
              <SelectedFiltersDropdown
                options={filterOptions}
                selectedFilters={filtersState.map((filter) => filter.id)}
                setSelectedFilters={handleSelectedFiltersChange}
                groupOrder={groupOrder}
              />
            )}
          </TableFiltersOptionsScroll>
        </TableFiltersOptions>
      )}
      {hasControls && (
        <ClearAccessLevelContext>
          <TableFiltersControls
            size="small"
            relation="separate"
            css={outerGutterCSS('end', viewGutter)}
          >
            {hasFilters && (
              <Button
                onClick={handleClearFilters}
                arrangement="hidden-label"
                appearance="muted"
                icon="trash-can"
                size="small"
                variant="neutral"
              >
                Clear filters
              </Button>
            )}
            {hasFilters && controls && (
              <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
            )}
            {controls && controls}
          </TableFiltersControls>
        </ClearAccessLevelContext>
      )}
    </TableFiltersContainer>
  );
}

// Enhanced interface for TableState integration
function TableSort<RowType, C extends SharedColumnDef<RowType, any>>({
  columns,
  tableState,
}: {
  columns: C[];
  tableState: AutoCollectionStateInterface<RowType, C['id']>;
}) {
  const sortableColumns = useMemo(
    () =>
      columns.filter(
        (col): col is C & { id: string } =>
          typeof col.id === 'string' &&
          (typeof col.header === 'string' || typeof col.meta?.label === 'string') &&
          col.enableSorting !== false,
      ),
    [columns],
  );

  if (sortableColumns.length === 0) return null;

  const currentSort = tableState.sortingState[0];
  const activeColumn = sortableColumns.find((c) => c.id === currentSort?.id) ?? sortableColumns[0];
  const isDesc = currentSort?.desc ?? false;

  return (
    <DropdownMenu>
      <DropdownMenuButton appearance="muted" arrangement="hidden-label" icon="arrows-up-down">
        Sort
      </DropdownMenuButton>
      <DropdownMenuPopover align="start">
        <DropdownMenuGroup label="Sort by">
          <DropdownMenuRadioGroup
            value={activeColumn.id}
            onValueChange={(newColumnId) => {
              tableState.setSortingState([{ id: newColumnId, desc: isDesc }]);
            }}
          >
            {sortableColumns.map((col) => (
              <DropdownMenuRadioItem
                key={col.id}
                value={col.id}
                label={(col.meta?.label ?? col.header) as string}
              />
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
        <DropdownMenuGroup label="Direction">
          <DropdownMenuRadioGroup
            value={isDesc ? 'desc' : 'asc'}
            onValueChange={(dir) => {
              tableState.setSortingState([{ id: activeColumn.id, desc: dir === 'desc' }]);
            }}
          >
            <DropdownMenuRadioItem value="asc" label="Ascending" icon="arrow-up" />
            <DropdownMenuRadioItem value="desc" label="Descending" icon="arrow-down" />
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
      </DropdownMenuPopover>
    </DropdownMenu>
  );
}

interface TableFiltersEnhancedBaseProps<RowType, C extends SharedColumnDef<RowType, any>> {
  columns?: C[];
  tableState: AutoCollectionStateInterface<RowType, C['id']>;
  /** Disables all filter fields. */
  disabled?: boolean;
  staticFilters?: ReactNode;
  controls?: ReactNode;
  overflowActions?: TableFiltersActions[];
  multiSelectActions?: ReactNode;
  /** When true, shows a sort dropdown in the filter bar. Intended for headerless layouts like AutoBlocks. */
  enableSortMenu?: boolean;
  /** When true, shows a customization menu in the filter bar. Intended for headerless layouts like AutoBlocks. */
  enableCustomizationMenu?: boolean;
  /** When true, shows a presets dropdown in the filter bar to save and select filter presets. */
  enablePresets?: boolean;
  /** When true, column reordering is disabled (no drag handles in Columns dropdown). */
  disableColumnOrder?: boolean;
  /** When true, options in the "Add a filter…" dropdown are sorted alphabetically within each group. */
  sortFilterOptions?: boolean;
  /** When true, forces the stacked narrow layout instead of deriving it from the measured width. */
  isNarrow?: boolean;
  /** gutter for the views and controls cells' outer edges. defaults to the application gutter. */
  viewGutter?: GutterConfig;
}

interface ClientTableFiltersEnhancedProps<RowType, C extends SharedColumnDef<RowType, any>>
  extends TableFiltersEnhancedBaseProps<RowType, C> {
  data?: RowType[];
  hideGlobalFilter?: boolean;
  disableCSVExport?: boolean;
  /** Shortcut priority for search (0 = ⌘F, 1 = ⌘G). Lower = primary. */
  shortcutPriority?: number;
}

interface ServerTableFiltersEnhancedProps<RowType, C extends SharedColumnDef<RowType, any>>
  extends TableFiltersEnhancedBaseProps<RowType, C> {
  table: Table<RowType>;
}

type TableFiltersEnhancedProps<RowType, C extends SharedColumnDef<RowType, any>> =
  | (ClientTableFiltersEnhancedProps<RowType, C> & { serverSide?: false })
  | (ServerTableFiltersEnhancedProps<RowType, C> & { serverSide: true });

function ClientTableFiltersEnhanced<RowType, C extends SharedColumnDef<RowType, any>>({
  columns = [],
  data,
  tableState,
  controls,
  staticFilters,
  multiSelectActions,
  hideGlobalFilter,
  disableColumnOrder = false,
  enableCustomizationMenu = true,
  enableSortMenu = false,
  enablePresets = true,
  shortcutPriority,
  disableCSVExport = false,
  overflowActions,
  sortFilterOptions = false,
  disabled,
  viewGutter,
}: ClientTableFiltersEnhancedProps<RowType, C>) {
  const optionsScroll = useOptionsScroll();
  const [openFilters, setOpenFilters] = useState<Set<string>>(new Set());

  const resolvedData = data ?? tableState.tableRef?.current?.data;

  const filters: FilterMapVal<RowType>[] = useMemo(() => {
    const filterList: FilterMapVal<RowType>[] = [];

    for (const col of columns) {
      // Add main column filter if it has filter configuration
      if (col && col.filterVariant) {
        const label = (col.filterLabel ?? col.meta?.label ?? col.header ?? '').toString();
        const value = tableState.columnFilters?.find((filter) => filter.id === col.id)
          ?.value as FilterValue;

        filterList.push({
          id: col.id,
          value,
          label,
          data: resolvedData,
          filter: col.filter,
          options: col.filterOptions,
          optionsLoading: col.filterOptionsLoading,
          variant: col.filterVariant,
          dateRangePresets: col.filterDateRangePresets,
          pinned: col.meta?.filterPinned,
          accessLevel: col.meta?.accessLevel,
        });
      }

      // Add subline filter if column has subline and sublineFilterVariant is defined
      if (
        col?.meta?.subline &&
        col.sublineFilterVariant &&
        (col.sublineFilterFn || col.sublineFilter)
      ) {
        const sublineId = `${col.id}-subline`;
        const baseLabel = col.filterLabel ?? col.meta?.label ?? col.header;
        const label = col.sublineFilterLabel ?? (baseLabel ? `${baseLabel} (subline)` : 'Subline');
        const value = tableState.columnFilters?.find((filter) => filter.id === sublineId)
          ?.value as FilterValue;

        filterList.push({
          id: sublineId,
          value,
          label,
          data: resolvedData,
          filter: col.sublineFilter,
          options: col.sublineFilterOptions,
          optionsLoading: col.sublineFilterOptionsLoading,
          variant: col.sublineFilterVariant,
          pinned: col.meta?.sublineFilterPinned,
          accessLevel: col.meta?.accessLevel,
        });
      }
    }

    return filterList;
  }, [columns, tableState.columnFilters, resolvedData]);

  const filtersMap = useMemo(() => {
    const map = new Map<string, FilterMapVal<RowType>>();
    for (const f of filters) {
      map.set(f.id, f);
    }
    return map;
  }, [filters]);

  const filterOptionsResult = useMemo(() => {
    const optionsMap = new Map<
      string,
      {
        variant: FilterVariant;
        value: string;
        label: string;
        group?: string | null;
        pinned?: boolean;
        accessLevel?: AccessLevelProp | null | undefined;
      }
    >();
    const groupOrderMap = new Map<string, number>();
    let columnIndex = 0;

    for (const col of columns) {
      let mainColumnAdded = false;

      // Add main column filter option
      if (col?.filterVariant && col.id) {
        const label = (col.filterLabel ?? col.header) as string;
        const group = col.meta?.group ?? null;
        const groupName = group ?? DEFAULT_FILTER_GROUP_NAME;

        // Track the first occurrence of each group
        if (!groupOrderMap.has(groupName)) {
          groupOrderMap.set(groupName, columnIndex);
        }

        optionsMap.set(col.id, {
          variant: col.filterVariant,
          value: col.id,
          label,
          group,
          pinned: col?.meta?.filterPinned,
          accessLevel: col?.meta?.accessLevel,
        });
        columnIndex += 1;
        mainColumnAdded = true;
      }

      // Add subline filter option if column has subline and sublineFilterVariant
      if (
        col?.meta?.subline &&
        col.sublineFilterVariant &&
        (col.sublineFilterFn || col.sublineFilter) &&
        col.id
      ) {
        const sublineId = `${col.id}-subline`;
        const baseLabel = col.filterLabel ?? col.header;
        const label = col.sublineFilterLabel ?? (baseLabel ? `${baseLabel} (subline)` : 'Subline');
        const group = col.meta?.group ?? null;
        const groupName = group ?? DEFAULT_FILTER_GROUP_NAME;

        // Track the first occurrence of each group
        // If main column was added, use its index; otherwise use current columnIndex
        if (!groupOrderMap.has(groupName)) {
          groupOrderMap.set(groupName, mainColumnAdded ? columnIndex - 1 : columnIndex);
        }

        optionsMap.set(sublineId, {
          variant: col.sublineFilterVariant,
          value: sublineId,
          label,
          group,
          pinned: col?.meta?.sublineFilterPinned,
          accessLevel: col?.meta?.accessLevel,
        });
      }
    }
    return { options: Array.from(optionsMap.values()), groupOrder: groupOrderMap };
  }, [columns]);

  const { options: filterOptions, groupOrder } = filterOptionsResult;

  const handleFiltersChange = useCallback(
    (id: string, value: FilterValue) => {
      let newFilters = [];
      const prevFilter = tableState.columnFilters?.find((col) => col.id === id);
      if (prevFilter) {
        newFilters =
          tableState.columnFilters?.map((col) => {
            if (col.id === id) {
              return {
                ...col,
                value,
              };
            }
            return { ...col };
          }) ?? [];
      } else {
        newFilters = [...(tableState.columnFilters ?? []), { id, value }];
      }

      const filterIDs = new Set(newFilters.map((f) => f.id));
      const pinnedFiltersToAdd = [];
      // check for pinned filters, add if they are missing
      for (const filterOption of filterOptions) {
        if (!filterOption.pinned) {
          continue;
        }

        if (filterIDs.has(filterOption.value)) {
          continue;
        }

        pinnedFiltersToAdd.push({
          id: filterOption.value,
          value: initValForVariant(filterOption.variant),
        });
      }

      if (pinnedFiltersToAdd.length > 0) {
        newFilters.splice(0, 0, ...pinnedFiltersToAdd);
      }

      tableState.setColumnFilters(newFilters);
    },
    [filterOptions, tableState],
  );

  const handleSelectedFiltersChange = useCallback(
    (ids: string[]) => {
      // Find newly added filters
      const currentFilterIds = new Set(tableState.columnFilters?.map((filter) => filter.id) || []);
      const newlyAddedFilters = ids.filter((id) => !currentFilterIds.has(id));

      const newFilters = [];
      for (const id of ids) {
        const prevFilter = tableState.columnFilters?.find((filter) => filter.id === id);
        if (prevFilter) {
          newFilters.push(prevFilter);
        } else {
          const newFilter = filtersMap.get(id);
          if (newFilter) {
            newFilters.push({
              id,
              value: initValForVariant(newFilter?.variant),
            });
          }
        }
      }

      // Automatically open newly added filters
      if (newlyAddedFilters.length > 0) {
        setOpenFilters((prev) => new Set([...prev, ...newlyAddedFilters]));
      }

      tableState.setColumnFilters(newFilters);
    },
    [filtersMap, tableState],
  );

  const handleExportToCSV = useCallback(() => {
    tableState.tableRef?.current?.exportToCSV();
  }, [tableState.tableRef]);

  const handleClearFilters = useCallback(() => {
    tableState.setColumnFilters([]);
  }, [tableState]);

  const handleClearFilter = useCallback(
    (id: string) => {
      const filteredIds =
        tableState.columnFilters?.filter((filter) => filter.id !== id).map((filter) => filter.id) ??
        [];
      handleSelectedFiltersChange(filteredIds);
    },
    [tableState, handleSelectedFiltersChange],
  );

  const handleFilterOpenChange = useCallback((id: string, isOpen: boolean) => {
    setOpenFilters((prev) => {
      const newSet = new Set(prev);
      if (isOpen) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }, []);

  const filtersStateWithOptions = useMemo(
    () =>
      tableState.columnFilters.map((filter) => {
        const mappedFilter = filtersMap.get(filter.id);
        const finalOptions = normalizeFilterOptions(
          data ?? tableState.tableRef?.current?.data,
          mappedFilter?.options,
        );

        return {
          ...filter,
          mappedFilter,
          finalOptions,
        };
      }),
    [data, filtersMap, tableState.columnFilters, tableState.tableRef],
  );

  const updateFilterValue = useEffectEvent((columnID: string, value: FilterValue) => {
    handleFiltersChange(columnID, value);
  });

  useEffect(() => {
    for (const { id, finalOptions, value, mappedFilter } of filtersStateWithOptions) {
      if (mappedFilter?.optionsLoading || !finalOptions || !Array.isArray(value)) continue;
      const optionsMap = new Map(finalOptions.map((opt) => [opt.value, opt]));
      const values = value
        .map((v) => optionsMap.get(v))
        .filter(isDefined)
        .map((opt) => opt.value);
      if (values.length !== value.length) {
        updateFilterValue(id, values);
      }
    }
  }, [filtersMap, columns, filtersStateWithOptions]);

  const hasColumns = Boolean(columns?.length !== 0);
  const hasFilters = Boolean(hasColumns && tableState.columnFilters?.length > 0);
  const [ref, { width }] = useMeasure();

  const isNarrow = Boolean(width && width < 700);

  const columnsForTableColumns = useMemo(
    () =>
      disableColumnOrder && columns?.length
        ? columns.map((col) => ({
            ...col,
            meta: {
              ...col.meta,
              fixedPosition: col.meta?.fixedPosition ?? ('as-in' as const),
            },
          }))
        : columns,
    [columns, disableColumnOrder],
  );

  const hasTableCustomization = Boolean(enableCustomizationMenu && columns.length > 0);
  const hasTableSorting = Boolean(enableSortMenu && columns.length > 0);
  const hasStartControls = Boolean(hasTableCustomization || hasTableSorting);
  const hasOverflowActions =
    (overflowActions?.length ?? 0) > 0 || (tableState.tableRef && !disableCSVExport);
  const hasControls = Boolean(controls || hasFilters || hasOverflowActions || !hideGlobalFilter);
  const canAddFilter = tableState.columnFilters.length < filterOptions.length;
  const showSelectedFiltersDropdown = Boolean(
    filters.length > 0 && (tableState.columnFilters.length === 0 || (isNarrow && canAddFilter)),
  );
  const showTableFiltersViews = Boolean(
    multiSelectActions ||
      enablePresets ||
      hasStartControls ||
      staticFilters ||
      showSelectedFiltersDropdown,
  );

  return (
    <TableFiltersContainer ref={ref} isNarrow={isNarrow}>
      {showTableFiltersViews && (
        <TableFiltersViews
          relation="separate"
          size="small"
          css={outerGutterCSS('start', viewGutter)}
        >
          {multiSelectActions}
          {enablePresets && tableState.presets && <TablePresets presets={tableState.presets} />}
          {hasStartControls && (
            <ControlGroup relation="joined" size="small">
              {hasTableCustomization && (
                <TableColumnsEnhanced<RowType>
                  columns={columnsForTableColumns}
                  tableState={tableState}
                />
              )}
              {hasTableSorting && <TableSort columns={columns} tableState={tableState} />}
            </ControlGroup>
          )}
          {staticFilters}
          {hasStartControls &&
            hasColumns &&
            filters.length > 0 &&
            (!isNarrow || showSelectedFiltersDropdown) && (
              <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
            )}
          {showSelectedFiltersDropdown && (
            <SelectedFiltersDropdown
              options={filterOptions}
              selectedFilters={tableState.columnFilters.map((filter) => filter.id)}
              setSelectedFilters={handleSelectedFiltersChange}
              groupOrder={groupOrder}
              sortOptions={sortFilterOptions}
              disabled={disabled}
            />
          )}
        </TableFiltersViews>
      )}
      {hasColumns && filters.length > 0 && tableState.columnFilters.length > 0 && (
        <TableFiltersOptions>
          <TableFiltersOptionsScroll
            isNarrow={isNarrow}
            css={
              isNarrow ? narrowOptionsCSS(viewGutter, optionsScroll.verticalOverflow) : undefined
            }
            hasViews
            hasControls={hasControls}
            ref={optionsScroll.ref}
            {...optionsScroll.horizontalScrollShadow}
          >
            <ClearAccessLevelContext>
              {filtersStateWithOptions.map(({ mappedFilter, finalOptions: options }) => {
                if (!mappedFilter) return null;

                const sharedTokenProps = {
                  handle: mappedFilter.label,
                  value: (
                    <DisplayForFilterValue
                      value={mappedFilter.value}
                      options={options}
                      variant={mappedFilter.variant}
                      loading={mappedFilter.optionsLoading}
                    />
                  ),
                  onTokenClear: () => handleClearFilter(mappedFilter.id),
                  modified: isFilterApplied(mappedFilter.value),
                  dismissable: !mappedFilter.pinned,
                } satisfies Partial<TableFiltersTokenProps>;

                if (mappedFilter.variant === 'boolean') {
                  // A boolean column with its own filter component gets a hybrid token: the
                  // toggle answers from the bar, and the dropdown holds the component's
                  // refinements of the toggled-on state (which replace the bare boolean).
                  if (mappedFilter.filter) {
                    return (
                      <TableFiltersToken
                        {...sharedTokenProps}
                        key={mappedFilter.id}
                        variant="toggle-dropdown"
                        onToggleClick={() => {
                          const turningOn = mappedFilter.value === false;
                          // Toggling off unmounts the refinement dropdown; forget its open state
                          // so switching back on doesn't pop it open unasked.
                          if (!turningOn) handleFilterOpenChange(mappedFilter.id, false);
                          handleFiltersChange(mappedFilter.id, turningOn);
                        }}
                        toggleSelected={mappedFilter.value !== false}
                        // An empty refinement renders as nothing, which would defeat the 'All'
                        // fallback; only a range with an end in it earns the display.
                        value={
                          Array.isArray(mappedFilter.value) &&
                          mappedFilter.value.some((end) => end != null)
                            ? sharedTokenProps.value
                            : null
                        }
                        changeable
                        isOpen={openFilters.has(mappedFilter.id)}
                        onOpenChange={(isOpen) => handleFilterOpenChange(mappedFilter.id, isOpen)}
                        disabled={disabled}
                      >
                        <TableFilter
                          mappedFilter={mappedFilter}
                          options={options}
                          handleFiltersChange={handleFiltersChange}
                        />
                      </TableFiltersToken>
                    );
                  }

                  return (
                    <TableFiltersToken
                      {...sharedTokenProps}
                      key={mappedFilter.id}
                      variant="toggle"
                      onToggleClick={() =>
                        handleFiltersChange(mappedFilter.id, !(mappedFilter.value as boolean))
                      }
                      toggleSelected={mappedFilter.value as boolean}
                    />
                  );
                }

                return (
                  <TableFiltersToken
                    {...sharedTokenProps}
                    key={mappedFilter.id}
                    changeable
                    isOpen={openFilters.has(mappedFilter.id)}
                    onOpenChange={(isOpen) => handleFilterOpenChange(mappedFilter.id, isOpen)}
                    disabled={disabled}
                  >
                    <TableFilter
                      mappedFilter={mappedFilter}
                      options={options}
                      handleFiltersChange={handleFiltersChange}
                    />
                  </TableFiltersToken>
                );
              })}
            </ClearAccessLevelContext>
            {!isNarrow && canAddFilter && (
              <SelectedFiltersDropdown
                options={filterOptions}
                selectedFilters={tableState.columnFilters.map((filter) => filter.id)}
                setSelectedFilters={handleSelectedFiltersChange}
                groupOrder={groupOrder}
                sortOptions={sortFilterOptions}
                disabled={disabled}
              />
            )}
          </TableFiltersOptionsScroll>
        </TableFiltersOptions>
      )}
      {hasControls && (
        <ClearAccessLevelContext>
          <TableFiltersControls
            size="small"
            relation="separate"
            css={outerGutterCSS('end', viewGutter)}
          >
            {hasFilters && (
              <Button
                onClick={handleClearFilters}
                appearance="muted"
                arrangement="hidden-label"
                icon="trash-can"
                size="small"
                variant="neutral"
                disabled={disabled}
              >
                Clear filters
              </Button>
            )}
            {hasFilters && controls && (
              <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
            )}
            {controls}
            {hasOverflowActions && (
              <DropdownMenu>
                <DropdownMenuButton
                  appearance="muted"
                  arrangement="hidden-label"
                  icon="overflow-horizontal"
                  variant="secondary"
                >
                  More actions
                </DropdownMenuButton>
                <DropdownMenuPopover>
                  <TableFiltersActionsMenu actions={overflowActions} />
                  {tableState.tableRef && !disableCSVExport && (
                    <DropdownMenuGroup>
                      <DropdownMenuItem
                        onClick={handleExportToCSV}
                        icon="export"
                        description={
                          tableState.isServerSidePaginated
                            ? 'Exports the current page of data only.'
                            : undefined
                        }
                        disabled={tableState.numRows === 0}
                      >
                        Export CSV
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  )}
                </DropdownMenuPopover>
              </DropdownMenu>
            )}
            {!hideGlobalFilter && (
              <KeyBoundSearchInput
                searchParamStateKey={`${tableState.storageKey}-filter`}
                shortcutPriority={shortcutPriority}
                aria-label="Filter"
                value={tableState.globalFilter}
                onChange={tableState.setGlobalFilter}
                disabled={disabled}
              />
            )}
          </TableFiltersControls>
        </ClearAccessLevelContext>
      )}
    </TableFiltersContainer>
  );
}

export interface SortColumnDefinition {
  name: string;
  value: string;
}

interface ServerSideTableSortProps<RowType> {
  table: Table<RowType>;
}

function ServerSideTableSort<RowType>({ table }: ServerSideTableSortProps<RowType>) {
  if (!table.sortDefinitions || table.sortDefinitions.length === 0) {
    return null;
  }

  const currentSort = table.getSortOrder();

  return (
    <DropdownMenu>
      <DropdownMenuButton appearance="muted" arrangement="hidden-label" icon="arrows-up-down">
        Sort
      </DropdownMenuButton>
      <DropdownMenuPopover align="start">
        <DropdownMenuGroup label="Sort by">
          <DropdownMenuRadioGroup
            value={currentSort.column}
            onValueChange={(newColumnId) => {
              table.setSortColumn(newColumnId);
            }}
          >
            {table.sortDefinitions?.map((definition) => (
              <DropdownMenuRadioItem
                key={definition.value}
                value={definition.value}
                label={definition.name}
              />
            )) ?? null}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
        <DropdownMenuGroup label="Direction">
          <DropdownMenuRadioGroup
            value={currentSort.orderBy}
            onValueChange={(dir) => {
              if (isTableSortOrder(dir)) {
                table.setSortOrderType(dir);
              }
            }}
          >
            <DropdownMenuRadioItem
              value={TableSortOrderType.Ascending}
              label="Ascending"
              icon="arrow-up"
            />
            <DropdownMenuRadioItem
              value={TableSortOrderType.Descending}
              label="Descending"
              icon="arrow-down"
            />
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
      </DropdownMenuPopover>
    </DropdownMenu>
  );
}

function ServerTableFiltersEnhanced<RowType, C extends SharedColumnDef<RowType, any>>({
  multiSelectActions,
  columns = [],
  enableCustomizationMenu = true,
  enableSortMenu = false,
  staticFilters,
  disableColumnOrder = false,
  controls,
  overflowActions,
  table,
  tableState,
  sortFilterOptions = false,
  disabled,
  isNarrow: isNarrowProp,
  viewGutter,
}: ServerTableFiltersEnhancedProps<RowType, C>) {
  const [ref, { width }] = useMeasure();
  const optionsScroll = useOptionsScroll();
  const isNarrow = isNarrowProp ?? (width !== null && width < 700);
  const hasTableCustomization = enableCustomizationMenu && columns.length > 0;
  const hasTableSorting = enableSortMenu && columns.length > 0;
  const hasStartControls = hasTableCustomization || hasTableSorting;
  const hasColumns = columns?.length !== 0;
  const hasOverflowActions = (overflowActions?.length ?? 0) > 0;
  const hasFilters = hasColumns && table.filters.length > 0;
  const hasControls = !!controls || hasFilters || hasOverflowActions;
  const orderedColumns = useMemo(
    () =>
      disableColumnOrder && columns?.length
        ? columns.map((col) => ({
            ...col,
            meta: {
              ...col.meta,
              fixedPosition: col.meta?.fixedPosition ?? ('as-in' as const),
            },
          }))
        : columns,
    [columns, disableColumnOrder],
  );
  const filterOptions: SelectedFiltersDropdownOption[] = useMemo(() => {
    if (!table.filterDefinitions) {
      return [];
    }
    return table.filterDefinitions.map((definition) => ({
      value: definition.id,
      label: definition.label,
      accessLevel: definition.accessLevel,
    }));
  }, [table.filterDefinitions]);
  const handleSelectedFilters = useCallback(
    (ids: string[]) => {
      table.setFilters(
        ids
          .map<Filter | undefined>((id) => {
            const filter = table.getFilterByID(id);
            if (filter) {
              return filter;
            }
            const definition = table.getFilterDefinition(id);
            if (!definition) {
              return undefined;
            }
            switch (definition.variant) {
              case TableFilterVariant.Bool:
                return {
                  key: definition.id,
                  type: definition.variant,
                  value: true,
                };
              case TableFilterVariant.Text:
                return {
                  key: definition.id,
                  type: definition.variant,
                  value: '',
                };
              case TableFilterVariant.Number:
                return {
                  key: definition.id,
                  type: definition.variant,
                  value: 0,
                };
              case TableFilterVariant.Select:
                return {
                  key: definition.id,
                  type: definition.variant,
                  value: '',
                };
              case TableFilterVariant.DateRange:
                return {
                  key: definition.id,
                  type: definition.variant,
                  start: null,
                  end: null,
                };
              case TableFilterVariant.NumberRange:
                return {
                  key: definition.id,
                  type: definition.variant,
                  start: null,
                  end: null,
                };
              case TableFilterVariant.MultiSelect:
                return {
                  key: definition.id,
                  type: definition.variant,
                  values: [],
                };
              default:
                return undefined;
            }
          })
          .filter((filter) => !!filter),
      );
    },
    [table],
  );
  const handleClearFilters = useCallback(() => {
    table.setFilters([]);
  }, [table]);
  const canAddFilter = table.filters.length < filterOptions.length;
  const showSelectedFiltersDropdown =
    filterOptions.length > 0 && (table.filters.length === 0 || (isNarrow && canAddFilter));

  return (
    <TableFiltersContainer ref={ref} isNarrow={isNarrow}>
      <TableFiltersViews relation="separate" size="small" css={outerGutterCSS('start', viewGutter)}>
        {multiSelectActions && multiSelectActions}
        {hasStartControls && (
          <ControlGroup relation="joined" size="small">
            {hasTableCustomization && (
              <TableColumnsEnhanced<RowType> columns={orderedColumns} tableState={tableState} />
            )}
            {hasTableSorting && <ServerSideTableSort table={table} />}
          </ControlGroup>
        )}
        {staticFilters && staticFilters}
        {hasColumns &&
          table.filterDefinitions &&
          table.filterDefinitions.length > 0 &&
          (!isNarrow || showSelectedFiltersDropdown) && (
            <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
          )}
        {showSelectedFiltersDropdown && (
          <SelectedFiltersDropdown
            options={filterOptions}
            selectedFilters={table.filters.map((filter) => filter.key)}
            setSelectedFilters={handleSelectedFilters}
            loading={table.filterDefinitions === undefined}
            sortOptions={sortFilterOptions}
          />
        )}
      </TableFiltersViews>
      {hasColumns &&
        table.filters.length > 0 &&
        table.filterDefinitions &&
        table.filterDefinitions.length > 0 && (
          <TableFiltersOptions>
            <TableFiltersOptionsScroll
              isNarrow={isNarrow}
              css={
                isNarrow ? narrowOptionsCSS(viewGutter, optionsScroll.verticalOverflow) : undefined
              }
              hasViews
              hasControls={hasControls}
              ref={optionsScroll.ref}
              {...optionsScroll.horizontalScrollShadow}
            >
              <ClearAccessLevelContext>
                {table.filters.map((filter) => (
                  <ServerTableFiltersToken key={filter.key} table={table} filter={filter} />
                ))}
              </ClearAccessLevelContext>
              {!isNarrow && canAddFilter && (
                <SelectedFiltersDropdown
                  options={filterOptions}
                  selectedFilters={table.filters.map((filter) => filter.key)}
                  setSelectedFilters={handleSelectedFilters}
                  loading={table.filterDefinitions === undefined}
                  sortOptions={sortFilterOptions}
                />
              )}
            </TableFiltersOptionsScroll>
          </TableFiltersOptions>
        )}
      {hasControls && (
        <ClearAccessLevelContext>
          <TableFiltersControls
            size="small"
            relation="separate"
            css={outerGutterCSS('end', viewGutter)}
          >
            {hasFilters && (
              <Button
                onClick={handleClearFilters}
                appearance="muted"
                arrangement="hidden-label"
                icon="trash-can"
                size="small"
                variant="neutral"
                disabled={disabled}
              >
                Clear filters
              </Button>
            )}
            {hasFilters && controls && (
              <TableFiltersDivider appearance="thick" direction="column" variant="neutral" />
            )}
            {controls && controls}
            {hasOverflowActions && (
              <DropdownMenu>
                <DropdownMenuButton
                  appearance="muted"
                  arrangement="hidden-label"
                  icon="overflow-horizontal"
                  variant="secondary"
                >
                  More actions
                </DropdownMenuButton>
                <DropdownMenuPopover>
                  <TableFiltersActionsMenu actions={overflowActions} />
                </DropdownMenuPopover>
              </DropdownMenu>
            )}
          </TableFiltersControls>
        </ClearAccessLevelContext>
      )}
    </TableFiltersContainer>
  );
}

// Enhanced component that works with TableState
export function TableFiltersEnhanced<RowType, C extends SharedColumnDef<RowType, any>>(
  props: TableFiltersEnhancedProps<RowType, C>,
) {
  if (!props.serverSide) {
    return <ClientTableFiltersEnhanced {...props} />;
  }
  return <ServerTableFiltersEnhanced {...props} />;
}
